import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Renderable, Labels, StatesObj } from '../app.models';
import {
  FormControlTypes,
  FormErrorConfig,
  FormInputTypes,
  FORMS_TOKEN,
  FormsConfig,
  FormsControlConfig,
  FormSelectOption,
  FormValidatorConfig,
  parentSelectionError,
} from './forms.models';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { StringUtils } from '../shared/utils/string-utils';
import { UtilsService } from '../services/utils.service';
import { isStatesAllowed } from '../config/app-config';
import { MAPIFieldErrorMessages } from './../app.models';
import { differenceInDays, parse } from 'date-fns';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { FormSearchUtils } from '../shared/utils/form-search-utils';
@Component({
  selector: 'app-forms',
  templateUrl: './forms.component.html',
  styleUrls: ['./forms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormsComponent implements OnInit, Renderable, OnDestroy {
  mapiFieldErrorMessages!: MAPIFieldErrorMessages;
  private _stateRequiredByTypes = ['DRV', 'STA'];
  isMobile$: Observable<boolean>;
  controlArray: any = [];
  selectedParentOption: any;
  expiryDateLabels: any;
  expiryDays: number;
  isExpiryError: boolean = false;
  formSearchUtils = new FormSearchUtils();
  countryArrowIcon: string = 'keyboard_arrow_down';

  private _config: FormsConfig;
  @Input() public set config(val: FormsConfig) {
    this.controlArray.push(val?.controls);
    this._config = val;

    if (
      this._config &&
      !!this._config.controls.find((control) => !this._form?.get(control.name))
    ) {
      this._buildForm();
      const isExpiryField =
        !!this.controlArray &&
        this.controlArray[0]?.some(
          (control: any) =>
            control.name === 'sender-primaryIdentification-expirationDate'
        );
      if (isExpiryField) {
        this.isExpiryError = true;
      }
    }
  }
  public get config() {
    return this._config;
  }

  public _labels: any;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.expiryDateLabels =
      this.labels?.stellar?.registration?.dateOfBirthComponent;
    this.mapiFieldErrorMessages =
      labels?.stellar?.gffp?.gffpLabels?.mapiFieldErrorMessages;
  }
  public get labels() {
    return this._labels;
  }

  @Output() public init: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();

  @ViewChildren(FormsComponent)
  childForms?: QueryList<FormsComponent>;
  @ViewChildren(MatAutocompleteTrigger)
  triggers!: QueryList<MatAutocompleteTrigger>;

  public expiryDate: FormControl;
  private _form!: FormGroup;
  public get form() {
    return this._form;
  }

  private _errors: FormErrorConfig = {};
  public get errors() {
    return this._errors;
  }

  public get isValid() {
    return (
      this._form?.valid &&
      (!this.childForms ||
        this.childForms.toArray().length === 0 ||
        !this.childForms
          .toArray()
          .find((childForm) => childForm.form && childForm.form.invalid))
    );
  }

  public get isInvalid() {
    return (
      (this._form && this._form.invalid) ||
      !!this.childForms
        ?.toArray()
        .find((childForm) => childForm.form && childForm.form.invalid) ||
      this.isExpiryError
    );
  }

  closeAllAutoCompletePanels() {
    this.triggers.forEach((trigger) => trigger.closePanel());
  }

  private _onInit$: Subject<void> = new Subject<void>();
  public readonly onInit$: Observable<void> = this._onInit$.asObservable();

  public readonly inputTypes: typeof FormControlTypes = FormControlTypes;

  private _watches: Subscription[] = [];

  constructor(
    public fb: FormBuilder,
    private utilsService: UtilsService,
    private _cdr: ChangeDetectorRef,
    @Optional() @Inject(FORMS_TOKEN) _injectedToken: FormsConfig
  ) {
    this._config = _injectedToken;
    this.isMobile$ = this.utilsService.isMobile;
  }

  public ngOnInit(): void {
    this._onInit$.next();
    this._onInit$.complete();
    this.init.emit(this._form);

    this.form
      .get('sender-primaryIdentification-expirationDate')
      ?.valueChanges.subscribe((date) => {
        const date1 = parse(date, 'yyyy-MM-dd', new Date());
        this.expiryDays = differenceInDays(new Date(), date1);
        if (this.expiryDays > 0) {
          this.form
            .get('sender-primaryIdentification-expirationDate')
            ?.setErrors({ invalidOption: true });
        } else {
          this.form
            .get('sender-primaryIdentification-expirationDate')
            ?.setErrors(null);
        }
      });
  }

  public ngOnDestroy(): void {
    this._watches.forEach((subscription) => {
      if (this._form && this._form.valid) {
        this._form.reset();
      }

      if (this.childForms) {
        this.childForms.toArray().forEach((childForm) => {
          if (childForm.form && childForm.form.valid) {
            childForm.form.reset();
          }
        });
      }
      subscription.unsubscribe();
    });
  }
  public renderPrimaryIdFieldErrorMessage(controlName: string): void {
    if (controlName && controlName === 'sender-primaryIdentification-id') {
      this.form.get(controlName)?.setErrors({ [controlName]: true });
      this._cdr.markForCheck();
    }
  }

  public getErrorMessage(
    controlName: string,
    errorName: string
  ): string | undefined {
    return this._errors[controlName]?.find(
      (errorConfig) => errorConfig.name === errorName
    )?.message;
  }

  // TODO -- {#TECHDEBT}, make this a little easier to determine without having to search thru an array
  public isRequired(validators: FormValidatorConfig[] = []): boolean {
    return !!validators?.find((validator) => validator.type === 'required');
  }

  public getSelectedOptionLabel(options: any, fieldValue: any) {
    if (!fieldValue) {
      return '';
    }
    const index = options?.findIndex(
      (option: any) => option.value === fieldValue
    );
    return options[index]?.label;
  }

  public onParentOptionChange(control: any, fieldValue: string) {
    if (control.name === 'sender-primaryIdentification-typeCode') {
      //reset country field on identification type change
      //this is need for rebuilding the child control
      this._form
        .get('sender-primaryIdentification-issueCountryCode')
        ?.setValue(null);
    }
  }

  public getValidatorFn(
    validator: FormValidatorConfig,
    inputType: FormInputTypes
  ): ValidatorFn {
    switch (validator.type) {
      case 'required':
        return Validators.required;
      case 'pattern':
        return Validators.pattern(validator.arg);
      case 'max':
        return inputType === 'text'
          ? (control: AbstractControl) => {
              return !control.value || control.value?.length <= validator.arg
                ? null
                : { max: true };
            }
          : Validators.max(validator.arg);
      case 'min':
        return inputType === 'text'
          ? (control: AbstractControl) => {
              return !control.value || control.value?.length >= validator.arg
                ? null
                : { min: true };
            }
          : Validators.min(validator.arg);
    }
  }

  public getParentSelectedOption(
    controlName: string,
    controlIndex: number
  ): FormSelectOption | undefined {
    const value = this._form.get(controlName)?.value;
    const parentOpt =
      (value &&
        this._config.controls[controlIndex]?.options?.find(
          (option) => option.value === value
        )) ||
      undefined;
    return this._form.get(parentOpt?.childControlName) ? parentOpt : undefined;
  }

  private _buildForm(): void {
    this._form = this.fb.group({});
    const controlErrors: FormErrorConfig = {};

    if (this._config && this._config.controls) {
      this._config.controls.forEach(
        (control: FormsControlConfig, controlIndex: number) => {
          if (control.validators) {
            controlErrors[control.name] = control.validators.map(
              (validator) => ({
                name: validator.type!,
                message: validator.message!,
              })
            );
          }

          const builtControl = this.fb.control(control.value, {
            validators: control.validators
              ? [
                  ...control.validators.map((validator) =>
                    this.getValidatorFn(validator, control.inputType!)
                  ),
                ]
              : [],
          });

          this._form.addControl(control.name, builtControl);

          // if this control is a select, check to see if any of its options have any children
          // (`FormSelectOption.options`)
          if (control.options) {
            const options = control && control.options ? control.options : [];
            control.filteredOptions = of(options);
            this._watches.push(
              builtControl.valueChanges.subscribe((value) => {
                const filterValue = value
                  ? StringUtils.removeDiacriticsAndConvertToLowercase(value)
                  : '';
                let filteredOptions: any = control?.options?.filter(
                  (option: any) => {
                    return (
                      option.label &&
                      StringUtils.removeDiacriticsAndConvertToLowercase(
                        option.label.toLowerCase()
                      ).startsWith(filterValue)
                    );
                  }
                );
                //This method handles the filtering and selection of options in a form control, and sets errors when appropriate(only for ID and country dropdown).
                this.handleParentFilterOptions(
                  filterValue,
                  control.name,
                  filteredOptions
                );
                filteredOptions = filteredOptions ? filteredOptions : [];
                control.filteredOptions = of(filteredOptions);
              })
            );
          }
          if (
            control.options &&
            !!control.options.find((opt) => opt.options?.length! > 0)
          ) {
            // check to see if control has a value and an option with children is selected
            if (builtControl.value) {
              const selectedOpt = control.options.find(
                (option) => option.value === builtControl.value
              );

              if (selectedOpt) {
                this._form.addControl(
                  selectedOpt.childControlName!,
                  this.fb.control(
                    undefined,
                    this.isRequired(control.validators)
                      ? [Validators.required]
                      : []
                  )
                );

                controlErrors[selectedOpt.childControlName!] = [
                  {
                    name: 'required',
                    message: `${selectedOpt.childLabel} ${this.labels.stellar.error.isRequired}`,
                  },
                ];
              }
            }

            // watch for control value changes so child can be shown/removed
            this._watches.push(
              builtControl.valueChanges.subscribe((parentValue) => {
                const parentOpt = this._config.controls[
                  controlIndex
                ]?.options?.find(
                  (option) =>
                    StringUtils.removeDiacriticsAndConvertToLowercase(
                      option?.label
                    ) ===
                    StringUtils.removeDiacriticsAndConvertToLowercase(
                      parentValue!
                    )
                );

                this.selectedParentOption = parentOpt;

                const typeCodeLabel = this._form.get(
                  'sender-primaryIdentification-typeCode'
                )?.value;
                let typeCode: string = '';

                // get typecode options to get the types
                const typeCodeControl = this._config.controls.find(
                  (control) =>
                    control.name === 'sender-primaryIdentification-typeCode'
                );
                if (typeCodeControl) {
                  const selectedOption = typeCodeControl.options?.find(
                    (option) => option.value === typeCodeLabel
                  );
                  typeCode = selectedOption ? selectedOption.value : '';
                }
                typeCode = typeCode ? typeCode : typeCodeLabel;

                // const isStateRequired = parentOpt?.childControlName === 'sender-primaryIdentification-issueCountrySubdivisionCode' ? this._stateRequiredByTypes.some(s => s === typeCode) : true;
                const selectedCountryCode = this._form.get(
                  'sender-primaryIdentification-issueCountryCode'
                )?.value;
                const isStateRequired =
                  selectedCountryCode &&
                  isStatesAllowed(selectedCountryCode) &&
                  this._stateRequiredByTypes.some((s) => s === typeCode);
                // add child select if its not on form already & needed
                if (
                  (parentOpt?.options?.length! > 0 &&
                    !this._form.get(parentOpt!.childControlName!) &&
                    isStateRequired) ||
                  isStateRequired
                ) {
                  const childBuiltControl = this.fb.control(
                    undefined,
                    // only require if parent is required
                    this.isRequired(control.validators)
                      ? [Validators.required]
                      : []
                  );
                  if (parentOpt?.options) {
                    const filteredOptions = [...parentOpt.options];
                    parentOpt.filteredOptions = of(filteredOptions);
                  }
                  this._form.addControl(
                    parentOpt!.childControlName!,
                    childBuiltControl
                  );

                  this._watches.push(
                    childBuiltControl.valueChanges.subscribe((value: any) => {
                      if (this.selectedParentOption?.options) {
                        const filterValue = value
                          ? StringUtils.removeDiacriticsAndConvertToLowercase(
                              value
                            )
                          : '';
                        let filteredOptions: any =
                          this.selectedParentOption?.options?.filter(
                            (option: any) => {
                              return (
                                option.label &&
                                StringUtils.removeDiacriticsAndConvertToLowercase(
                                  option.label
                                ).startsWith(filterValue)
                              );
                            }
                          );
                        const errorKey = ['required', 'stateNotFoundError'];
                        this.formSearchUtils.handleFilteredStateOption(
                          filterValue,
                          this.selectedParentOption.childControlName,
                          filteredOptions,
                          this.form,
                          this.triggers,
                          errorKey
                        );
                        filteredOptions = filteredOptions
                          ? filteredOptions
                          : [];
                        this.selectedParentOption.filteredOptions =
                          of(filteredOptions);
                      }
                    })
                  );

                  // add error messaging only once
                  if (!controlErrors[parentOpt!.childControlName!]) {
                    controlErrors[parentOpt!.childControlName!] = [
                      {
                        name: 'required',
                        message: `${parentOpt!.childLabel} ${
                          this.labels.stellar.error.isRequired
                        }`,
                      },
                    ];
                  }
                  // remove control
                } else if (this._form.get(parentOpt?.childControlName!)) {
                  this._form.removeControl(parentOpt!.childControlName!);
                }
              })
            );
          }

          this._errors = controlErrors;

          if (control.disabled) {
            builtControl.disable();
          }
        }
      );
    }
  }
  private handleParentFilterOptions(
    filterValue: string,
    controlName: string,
    filteredOptions: any
  ) {
    const error = this.handleParentSelectionError(controlName);
    if (filterValue && !filteredOptions.length) {
      this.form.get(controlName)?.setErrors(error);
    } else if (
      filterValue ===
      StringUtils.removeDiacriticsAndConvertToLowercase(
        filteredOptions[0]?.label
      )
    ) {
      const matchedOptions = filteredOptions.filter(
        (option: any) =>
          StringUtils.removeDiacriticsAndConvertToLowercase(option.label) ===
          filterValue
      );
      if (matchedOptions.length > 1) {
        const selectedOption =
          matchedOptions.find(
            (option: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                option.label
              ) === 'united states of america'
          ) || matchedOptions[0];
        this.form
          .get(controlName)
          ?.setValue(selectedOption.value, { emitEvent: false });
        this.closePanel();
      } else if (matchedOptions.length === 1) {
        this.form
          .get(controlName)
          ?.setValue(matchedOptions[0].value, { emitEvent: false });
        this.closePanel();
      }
      this.form.get(controlName)?.setErrors(null);
    } else if (!filterValue && filteredOptions.length > 0) {
      this.form.get(controlName)?.setErrors({ required: true });
    } else {
      this.form.get(controlName)?.setErrors(error);
    }
  }
  private handleParentSelectionError(
    controlName: string
  ): parentSelectionError {
    return controlName === 'sender-primaryIdentification-typeCode'
      ? { invalidOption: true }
      : { countryNotFoundError: true };
  }
  private closePanel() {
    this.triggers.forEach((trigger: MatAutocompleteTrigger) => {
      if (trigger.panelOpen) {
        setTimeout(() => trigger.closePanel());
      }
    });
  }
}
